// Maps an Operations tool call's input (get_operations_summary / get_operations_items /
// get_operations_journeys) to an
// Operations screen URL (useOperationsUrlState.ts param names), so "Show on the dashboard" applies
// the same slicers and opens the drawer on the same drill.

const ARRAY_KEY_MAP: Record<string, string> = {
  client: "client",
  requestType: "requestType",
  lineOfBusiness: "lob",
  engagementType: "eng",
  assignedTo: "assigned",
  team: "team",
  division: "division",
  manager: "manager",
  workerCountry: "country",
};

const DRILL_KEY_MAP: Record<string, string> = {
  attention: "dAttention",
  stuckBy: "dStuckBy",
  stage: "dStage",
  status: "dStatus",
  owner: "dOwner",
  workType: "dWorkType",
  flow: "dFlow",
  day: "dDay",
  dayKind: "dDayKind",
  startRisk: "dStartRisk",
};

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((v) => v.length > 0);
  if (typeof value === "string" && value.length > 0) return [value];
  return [];
}

/**
 * Builds an `/operations?…` path from a tool call's parsed `input`, or `null` when nothing maps.
 * A custom period cannot be expressed in the URL today, so it is left out (the screen falls back
 * to week to date, and the answer's own text still states the period it used).
 */
export function assistantFiltersToOperationsUrl(input: unknown): string | null {
  if (input === null || typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;
  const params = new URLSearchParams();

  if (obj.period === "today" || obj.period === "wtd") params.set("period", obj.period);
  if (obj.lens === "stuck" || obj.lens === "blocked" || obj.lens === "stuckAndBlocked" || obj.lens === "clear") params.set("lens", obj.lens);
  if (obj.pen === "internal" || obj.pen === "external") params.set("pen", obj.pen);

  for (const [toolKey, urlKey] of Object.entries(ARRAY_KEY_MAP)) {
    for (const v of asStringArray(obj[toolKey])) params.append(urlKey, v);
  }
  for (const flag of ["onboardingsOnly", "redeployment"] as const) {
    if (obj[flag] === true) params.set(flag, "true");
  }

  for (const [toolKey, urlKey] of Object.entries(DRILL_KEY_MAP)) {
    const v = obj[toolKey];
    if (typeof v === "string" && v.length > 0) params.set(urlKey, v);
  }
  if (typeof obj.ageBin === "number" && Number.isInteger(obj.ageBin) && obj.ageBin >= 1) {
    // An age bar opens the journey drawer, whatever flow the call carried.
    params.set("dAgeBin", String(obj.ageBin));
    params.delete("dFlow");
  } else if (typeof obj.flow === "string" && (obj.workType === "Onboarding" || obj.workType === "Redeployment")) {
    // A journey row of the Flow card: the journey drawer, not the request drawer.
    params.delete("dFlow");
    params.set("dJourneyFlow", obj.flow);
  }

  if ([...params.keys()].length === 0) return null;
  return `/operations?${params.toString()}`;
}

/**
 * A get_briefing call's link (2026-10-29): the scope's slicers, then the journey drawer on the open journeys the
 * briefing ranked, on the Breached / At risk cohort when the call listed one state. The briefing ignores the
 * lenses like the tiles do, so they are dropped; without this the link was the page the user was already on.
 */
export function assistantBriefingToOperationsUrl(input: unknown): string | null {
  if (input === null || typeof input !== "object") return null;
  const { lens: _lens, pen: _pen, state, ...rest } = input as Record<string, unknown>;
  const slicers = Object.fromEntries(Object.entries(rest).filter(([key]) => !(key in DRILL_KEY_MAP) && key !== "ageBin"));
  const base = assistantFiltersToOperationsUrl(slicers);
  const params = new URLSearchParams(base ? base.slice("/operations?".length) : "");
  params.set("dJourneyFlow", "open");
  if (state === "breached" || state === "atRisk") params.set("dStartRisk", state);
  return `/operations?${params.toString()}`;
}
